import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { Link, Redirect } from "react-router-dom";
import { FC, useEffect, useState } from "react";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { UploadTagsWizardSteps } from "@app/tags/import/types";
import UploadTagsWizardImportList from "@app/tags/import/UploadTagsWizardImportList";
import UploadTagsWizardReview from "@app/tags/import/UploadTagsWizardReview";

const UploadTagsWizard: FC = () => {
  const title = "Upload Tags";
  const resourceTaggingService = new ResourceTaggingService();
  const humanReadablePageNames = {
    [UploadTagsWizardSteps.ImportList]: "Import List",
    [UploadTagsWizardSteps.Review]: "Review",
  }

  const [inaccessibleProjectNames, setInaccessibleProjectNames] = useState<string[]>([]);
  const [page, setPage] = useState(UploadTagsWizardSteps.ImportList);
  const [redirectToView, setRedirectToView] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [tagsFile, setTagsFile] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  // Handlers
  const handleNext = (): void => {
    switch (page) {
      case UploadTagsWizardSteps.ImportList:
        setPage(UploadTagsWizardSteps.Review);
        break;
    }
  }

  const handlePrev = (): void => {
    switch (page) {
      case UploadTagsWizardSteps.Review:
        setPage(UploadTagsWizardSteps.ImportList);
        break;
    }
  }

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(UploadTagsWizardSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as UploadTagsWizardSteps;
    setPage(UploadTagsWizardSteps[step] as unknown as UploadTagsWizardSteps);
  };

  const handleUpload = async (data: FormData): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await resourceTaggingService.previewCreateAndApplyProjectTags(data);
        setInaccessibleProjectNames([]);
        setReviewData(response);
        setTagsFile(data);
        resolve();
      } catch (error) {
        if (error.responseJSON != undefined) {
          reject("Some project names were not found or were duplicated on this client. Please fix or remove missing or duplicate Project Names and re-upload.");
          setInaccessibleProjectNames(error.responseJSON);
          setReviewData(null);
          setTagsFile(null);
        }
        else {
          reject(error.responseText);
          setInaccessibleProjectNames([]);
          setReviewData(null);
          setTagsFile(null);
        }
      }
    });
  };

  const handleSubmit = async () => {
    try {
      if (tagsFile == null || inaccessibleProjectNames.length !== 0 || page !== UploadTagsWizardSteps.Review)
        return false;
      await resourceTaggingService.createAndApplyProjectTags(tagsFile);
      setRedirectToView(true);
      return true;
    } catch (error) {
      return false;
    }
  };

  const renderCurrentWizardStep = () => {
    switch (page) {
      case UploadTagsWizardSteps.ImportList:
        return <UploadTagsWizardImportList handleUpload={handleUpload} wasSuccessful={!!tagsFile} />;
      case UploadTagsWizardSteps.Review:
        return <UploadTagsWizardReview reviewData={reviewData} />;
    }
  };

  const canProceed = (): boolean => {
    return inaccessibleProjectNames.length == 0 && reviewData != null;
  }

  if (redirectToView) {
    return <Redirect to="/tags/view" push={false} />;
  }
  return (
    <Page documentTitle={title}>
      {/* The design for the wizard does not have breadcrumbs */}
      <PageInner className="upload-tags-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">{title}</h1>
        </header>
        <WizardSteps currentStep={page} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />
        {inaccessibleProjectNames.length > 0 && (
          <QESAlert classNames="mt-3 mb-3" type="warning" collapseTitle="The following project names were not found or were duplicated on this client. Please fix or remove missing or duplicate Project Names and re-upload.">
            <ul className="mb-0">
              {inaccessibleProjectNames.map((name) => <li key={`upload-tags-wizard__inaccessible-project-names__${name}`}>{name}</li>)}
            </ul>
          </QESAlert>
        )}
        <hr />
        {renderCurrentWizardStep()}
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/tags/view" className="qes-button qes-button-secondary danger" data-testid="upload-tags-wizard__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="upload-tags-wizard__nav-buttons">
            {page !== UploadTagsWizardSteps.ImportList &&
              <QESButton qesType="secondary" onClick={handlePrev} qesTestId="upload-tags-wizard__previous">Previous</QESButton>
            }
            {page !== UploadTagsWizardSteps.Review &&
              <QESButton onClick={handleNext} disabled={!canProceed()} qesTestId="upload-tags-wizard__next" className="ms-3">Next</QESButton>
            }
            {page === UploadTagsWizardSteps.Review &&
              <QESButton onClick={handleSubmit} disabled={tagsFile == null} qesTestId="upload-tags-wizard__submit" className="ms-3">Submit</QESButton>
            }
          </div>
        )}
      />
    </Page>
  );
}

export default UploadTagsWizard;
